import type { ItemUI } from '@libs/common-node';

import { useCart } from './useCart';

type Frequency = Pick<ItemUI, 'autoshipFrequency' | 'autoshipFrequencyUnitOfMeasure'>;
type PreviewItem = Pick<ItemUI, 'id' | 'listPrice' | 'quantity'>;

type UseOptimizelyFreeShipThresholdParams = Frequency & {
    previewItem?: PreviewItem;
};

const FREE_SHIPPING_THRESHOLD = 49;
const AUTOSHIP_DISCOUNT_MULTIPLIER = 0.95;

export const useOptimizelyFreeShipThreshold = ({
    autoshipFrequency,
    autoshipFrequencyUnitOfMeasure,
    previewItem,
}: UseOptimizelyFreeShipThresholdParams) => {
    const { cart } = useCart();

    const autoshipSubtotal =
        cart?.fulfillmentGroups
            ?.flatMap(({ items }) => items)
            .filter(item => item.id !== previewItem?.id)
            .filter(
                item =>
                    Number(item.autoshipFrequency) === Number(autoshipFrequency) &&
                    Number(item.autoshipFrequencyUnitOfMeasure) === Number(autoshipFrequencyUnitOfMeasure)
            )
            .reduce((subtotal, { listPrice, quantity }) => subtotal + listPrice * quantity, 0) ?? 0;

    const previewSubtotal = previewItem ? previewItem.listPrice * previewItem.quantity : 0;
    const discountedAutoshipSubtotal = (autoshipSubtotal + previewSubtotal) * AUTOSHIP_DISCOUNT_MULTIPLIER;
    const shipThreshold = Math.max(
        0,
        Math.floor((FREE_SHIPPING_THRESHOLD - discountedAutoshipSubtotal) * 100) / 100
    ).toFixed(2);

    return shipThreshold;
};
